import * as Cmp from "@impero-com/common/utils/comparison";
import {
    DataExtractor,
    ExtractorExpander,
    PartialData,
} from "@impero-com/common/utils/dataView";
import {
    ExtractorsAndExpanders,
    buildExtractorsAndExpanders,
} from "@impero-com/common/utils/dataView/util";
import {
    SimpleAndFilter,
    SimpleOrFilter,
} from "@impero-com/common/utils/indexing";
import { keyBy } from "lodash-es";

import { getTagsByCategory, TagListProps } from "components/tag/tagList";
import { TagsData } from "components/tag/types";
import { IdAndName } from "org/pageState/models";
import { Guid } from "utils/guid";
import { TAG_CATEGORIES_ID } from "utils/tagConstants";

const CATEGORY_EXTRACTOR_ID_PREFIX = "category_";

export interface TagLookupData<
    TAG extends AbstractTag,
    CAT extends AbstractTagCategory,
> {
    content: { resource: TAG }[];
    linkedResources: CAT[];
}

interface AbstractTagCategory {
    id: Guid;
    name: string;
}

interface AbstractTag {
    id: Guid;
    name: string;
}

interface CollectedCategories<
    CAT extends AbstractTagCategory,
    TAG extends AbstractTag,
> {
    categories: Array<CollectedCategory>;
    tagCategoriesById: Record<Guid, CAT>;
    tagsById: Record<Guid, TAG>;
    tagIdsByLowercaseCategoryName: Record<string, Set<Guid>>;
}

interface CollectedCategory {
    // ID of the category as in the database
    id: string;
    // Name of the category as in the database
    name: string;
    // Tags found in the data for the matching category
    tagNames: Array<string>;
}

interface CollectCategoriesArgs<
    ROW,
    CAT extends AbstractTagCategory,
    TAG extends AbstractTag,
> {
    getControlResultMetadata: (row: ROW) => Record<string, Array<string>>;
    rows: Array<ROW>;
    tagsDto: TagLookupData<TAG, CAT>;
    getCategoryIdFromTag: (tag: TAG) => string;
    isCategoryActive: (category: CAT) => boolean;
}

function collectCategories<
    ROW,
    CAT extends AbstractTagCategory,
    TAG extends AbstractTag,
>(args: CollectCategoriesArgs<ROW, CAT, TAG>): CollectedCategories<CAT, TAG> {
    const {
        getControlResultMetadata,
        rows,
        tagsDto,
        getCategoryIdFromTag,
        isCategoryActive,
    } = args;

    // Collect all the  categories found in the data, some of them may have been renamed/deleted compared
    const foundCatNameToCategory: Map<
        string,
        { name: string; tagNames: Set<string> }
    > = new Map();
    rows.forEach((row) => {
        const metadata = getControlResultMetadata(row);
        Object.entries(metadata).forEach(([categoryName, tagNamesArray]) => {
            const tagNames = new Set(tagNamesArray);
            const lcCategoryName = categoryName.toLowerCase();
            const foundCategory = foundCatNameToCategory.get(lcCategoryName);
            if (foundCategory === undefined) {
                foundCatNameToCategory.set(lcCategoryName, {
                    name: categoryName,
                    tagNames,
                });
            } else {
                // Unify early
                for (const tagName of tagNames) {
                    foundCategory.tagNames.add(tagName);
                }
            }
        });
    });

    // Collect all the  categories found in the data, some of them may have been renamed/deleted compared
    const catNameToCategory: Map<string, CollectedCategory> = new Map();

    const tagIdsByLowercaseCategoryName: Record<string, Set<Guid>> = {};

    // Overwrite found categories without mercy
    tagsDto.linkedResources.forEach((category) => {
        const lcCategoryName = category.name.toLowerCase();

        const catTags = tagsDto.content
            .filter((tag) => getCategoryIdFromTag(tag.resource) === category.id)
            .map((tag) => tag.resource);
        const catTagIds = catTags.map((tag) => tag.id);
        const catTagNames = catTags.map((tag) => tag.name);
        tagIdsByLowercaseCategoryName[lcCategoryName] = new Set(catTagIds);

        const existingCat = catNameToCategory.get(lcCategoryName);
        if (existingCat) {
            throw new Error(
                `Duplicate ${category.name} (ID ${category.id}) with name ${existingCat.name} and ID ${existingCat.id}`
            );
        }

        const foundCategory = foundCatNameToCategory.get(lcCategoryName);
        const isActive = isCategoryActive(category);
        if (isActive || foundCategory) {
            let tagNames: Array<string>;
            if (foundCategory) {
                tagNames = isActive
                    ? Array.from(foundCategory.tagNames).concat(catTagNames)
                    : Array.from(foundCategory.tagNames);
            } else {
                tagNames = catTagNames;
            }
            catNameToCategory.set(lcCategoryName, {
                id: category.id,
                name: category.name,
                tagNames,
            });
        }
    });

    const sortedCatNames = Array.from(catNameToCategory.keys());
    sortedCatNames.sort(Cmp.primitive);
    const cats: Array<CollectedCategory> = [];
    const stringCompare = Cmp.string();
    sortedCatNames.forEach((lcCategoryName) => {
        // Safety: the category is guaranteed to be present in `catNameToCategory`,
        // because `lcCategoryName` is a key of `catNameToCategory`.
        const category = catNameToCategory.get(lcCategoryName)!;
        const seen = new Set();
        const tagNames: Array<string> = [];
        category.tagNames.forEach((tagName) => {
            const lcTagName = tagName.toLowerCase();
            if (!seen.has(lcTagName)) {
                seen.add(lcTagName);
                tagNames.push(tagName);
            }
        });
        tagNames.sort(stringCompare);
        category.tagNames = tagNames;
        cats.push(category);
    });

    const tagCategoriesById = keyBy(tagsDto.linkedResources, (cat) => cat.id);
    const tagsById = keyBy(
        tagsDto.content.map((tag) => tag.resource),
        (tag) => tag.id
    );
    return {
        categories: cats,
        tagCategoriesById,
        tagsById,
        tagIdsByLowercaseCategoryName,
    };
}

const getTagMethods = (categoryPrefix: string) => {
    const isCategoryExtractorId = (extractorId: string): boolean => {
        return extractorId.startsWith(categoryPrefix);
    };
    const categoryIdToExtractorId = (categoryId: string): string => {
        return categoryPrefix + categoryId;
    };
    const extractorIdToCategoryId = (extractorId: string): string => {
        if (isCategoryExtractorId(extractorId)) {
            return extractorId.replace(categoryPrefix, "");
        } else {
            throw new Error(
                `ExtractorId "${extractorId}" does not start with "${categoryPrefix}"`
            );
        }
    };
    const abstractTagCategoryToExtractorId = (
        category: AbstractTagCategory
    ): string => {
        const { id, name } = category;
        return categoryPrefix + id + "_" + encodeHex(name);
    };
    const extractorIdToAbstractTagCategory = (
        extractorId: string
    ): AbstractTagCategory => {
        if (isCategoryExtractorId(extractorId)) {
            const idAndHexName = extractorId.replace(categoryPrefix, "");
            const [id, hexName] = idAndHexName.split("_");
            const name = decodeHex(hexName);
            return { id, name };
        } else {
            throw new Error(
                `ExtractorId "${extractorId}" does not start with "${categoryPrefix}"`
            );
        }
    };

    return {
        isCategoryExtractorId,
        categoryIdToExtractorId,
        extractorIdToCategoryId,
        abstractTagCategoryToExtractorId,
        extractorIdToAbstractTagCategory,
    };
};

const DEFAULT_CATEGORY_METHODS = getTagMethods(CATEGORY_EXTRACTOR_ID_PREFIX);

function categoryExtractorId(category: CollectedCategory) {
    return DEFAULT_CATEGORY_METHODS.abstractTagCategoryToExtractorId(category);
}

function encodeHex(plainValue: string): string {
    const textEncoder = new TextEncoder();
    const bytes: Uint8Array = textEncoder.encode(plainValue);
    const numbers: Array<number> = [...bytes];
    return numbers.map((b) => b.toString(16).padStart(2, "0")).join("");
}

function decodeHex(hexValue: string): string {
    if (!/^([0-9a-f]{2})*$/.test(hexValue)) {
        throw new Error(
            `decodeHex() fails to decode invalid hex value: ${hexValue}`
        );
    }
    const numbers: Array<number> = [];
    for (let i = 0; i < hexValue.length; i += 2) {
        numbers.push(parseInt(hexValue.substring(i, i + 2), 16));
    }
    const textDecoder = new TextDecoder();
    const bytes = new Uint8Array(numbers);
    return textDecoder.decode(bytes);
}

function isTagCategoriesExtractorId(extractorId: string) {
    return extractorId === TAG_CATEGORIES_ID;
}

function buildCategoryExtractorsAndExpanders<
    ROW,
    CAT extends AbstractTagCategory,
    TAG extends AbstractTag,
>(
    collectedCategories: CollectedCategories<CAT, TAG>,
    getTagValues: (
        row: ROW,
        categoryTagIds: Set<Guid>,
        lcCategoryName: string
    ) => Array<string>
): ExtractorsAndExpanders<ROW, string> {
    return buildExtractorsAndExpanders(
        collectedCategories.categories,
        (row: ROW, category: CollectedCategory): Array<string> => {
            const lcCategoryName = category.name.toLowerCase();
            const categoryTagIds =
                collectedCategories.tagIdsByLowercaseCategoryName[
                    lcCategoryName
                ];
            return getTagValues(row, categoryTagIds, lcCategoryName);
        },
        (tag) => tag,
        categoryExtractorId,
        (category) => category.name,
        (tag) => tag
    );
}

function buildTagCategoryExtractorExpanderForNonGroupedTagsWithIds<
    TAG extends AbstractTag,
    CAT extends AbstractTagCategory,
>(
    allTags: TagLookupData<TAG, CAT>
): ExtractorExpander<TagListProps, IdAndName<string, string>> {
    const categoryNameLookup = createCategoryNameLookupFn(allTags);

    const result: ExtractorExpander<TagListProps, IdAndName<string, string>> = {
        extractorId: TAG_CATEGORIES_ID,
        expand: (tags: TagListProps) => {
            const results: Array<PartialData<IdAndName<string, string>>> = [];
            const sortedTagsByCategory = getTagsByCategory(
                tags.tags,
                tags.tagCategories
            );
            sortedTagsByCategory.forEach(({ category, tags }) => {
                tags.forEach((tag) => {
                    // We are only interested in category names.
                    // However, a CR tagged with N tags in the same
                    // category should count N times for this
                    // category, not 1 time
                    const idAndName: IdAndName<string, string> = {
                        id: category.id,
                        name: category.name,
                    };
                    results.push({
                        partialId: `${category.name}-${tag.name}`,
                        expandedValue: idAndName,
                    });
                });
            });
            return results;
        },
        compare: Cmp.undefinedLast(
            Cmp.map((idAndName) => idAndName.name, Cmp.string())
        ),
        asString: categoryNameLookup,
        customFilterTransformer: {
            getValue: (idAndName: IdAndName<string, string>) => idAndName,
            asString: categoryNameLookup,
        },
    };
    return result;
}

// Returns a function that is used in place of `asString`.
//
// IdAndName represents category id and category name in the saved filters.
// The following situations are possible:
// * A category is renamed
//   * In this case we match the category by id and use its new name.
// * A category is deleted
//   * In this case we use name as it was saved in the filters.
function createCategoryNameLookupFn<
    TAG extends AbstractTag,
    CAT extends AbstractTagCategory,
>(
    allTags: TagLookupData<TAG, CAT>
): (idAndName: IdAndName<string, string>) => string {
    const idToNameMap: Record<string, string> = {};
    allTags.linkedResources.forEach((category) => {
        idToNameMap[category.id] = category.name;
    });
    return (idAndName: IdAndName<string, string>): string => {
        return idToNameMap[idAndName.id] || idAndName.name;
    };
}

// Similar to createCategoryNameLookupFn(), but for tags.
function createTagNameLookupFn<
    TAG extends AbstractTag,
    CAT extends AbstractTagCategory,
>(
    allTags: TagLookupData<TAG, CAT>
): (idAndName: IdAndName<string, string>) => string {
    const idToNameMap: Record<string, string> = {};
    allTags.content.forEach((tagDto) => {
        const tag = tagDto.resource;
        idToNameMap[tag.id] = tag.name;
    });
    return (idAndName: IdAndName<string, string>): string => {
        return idToNameMap[idAndName.id] || idAndName.name;
    };
}

// TODO: We should get rid of this in favor of buildTagCategoryExtractorExpanderForNonGroupedTagsWithIds,
// when tag categories on all pages are using IdAndName instead of simple strings.
function buildTagCategoryExtractorExpanderForNonGroupedTags(): ExtractorExpander<
    TagListProps,
    string
> {
    return {
        extractorId: TAG_CATEGORIES_ID,
        expand: (tags: TagListProps) => {
            const results: Array<PartialData<string>> = [];
            const sortedTagsByCategory = getTagsByCategory(
                tags.tags,
                tags.tagCategories
            );

            sortedTagsByCategory.forEach(({ category, tags }) => {
                tags.forEach((tag) => {
                    // We are only interested in category names.
                    // However, a CR tagged with N tags in the same
                    // category should count N times for this
                    // category, not 1 time
                    results.push({
                        partialId: `${category.name}-${tag.name}`,
                        expandedValue: category.name,
                    });
                });
            });
            return results;
        },
        compare: Cmp.undefinedLast(Cmp.string()),
        asString: (cat: string) => cat,
    };
}

function buildTagCategoryExtractorExpanderForGroupedTags(): ExtractorExpander<
    Record<string, Array<string>>,
    string
> {
    return {
        extractorId: TAG_CATEGORIES_ID,
        expand: (groupedTags: Record<string, Array<string>>) => {
            const results: Array<PartialData<string>> = [];

            Object.entries(groupedTags).forEach(([categoryName, tags]) => {
                tags.forEach((tag) => {
                    // We are only interested in category names.
                    // However, a CR tagged with N tags in the same
                    // category should count N times for this
                    // category, not 1 time
                    results.push({
                        partialId: `${categoryName}-${tag}`,
                        expandedValue: categoryName,
                    });
                });
            });
            return results;
        },
        compare: Cmp.undefinedLast(Cmp.string()),
        asString: (cat: string) => cat,
    };
}

// Context: extractorId for tag categories is built that way that it encodes category id and category name (see abstractTagCategoryToExtractorId function).
// In case if category name is renamed, extractorIds created from the stored filter may not longer match extractorIds generated from the loaded tags.
// This function adjusts extractorIds of stored filters.
//
// If stored filters contain extractor ids, which start with `category_` then:
// * Parse the category extractor id and get tag category id
// * Find a matching category from the loaded allTags
// * Build a new categoryId using the name the name of category.
// Otherwise use the same orFilter
function adjustFiltersWithCategoryExtractorId<
    TAG extends AbstractTag,
    CAT extends AbstractTagCategory,
>(
    allTags: TagLookupData<TAG, CAT>,
    storedFilter: SimpleAndFilter | null
): SimpleAndFilter | null {
    if (!storedFilter) {
        return null;
    }

    const newOrFilters: SimpleOrFilter[] = [];
    storedFilter.filters.forEach((storedOrFilter) => {
        if (
            DEFAULT_CATEGORY_METHODS.isCategoryExtractorId(
                storedOrFilter.extractorId
            )
        ) {
            const { id } =
                DEFAULT_CATEGORY_METHODS.extractorIdToAbstractTagCategory(
                    storedOrFilter.extractorId
                );
            const foundCategory = allTags.linkedResources.find(
                (c) => c.id === id
            );
            if (foundCategory) {
                const newExtractorId: string =
                    DEFAULT_CATEGORY_METHODS.abstractTagCategoryToExtractorId({
                        id: foundCategory.id,
                        name: foundCategory.name,
                    });
                const newOrFilter = {
                    ...storedOrFilter,
                    extractorId: newExtractorId,
                };
                newOrFilters.push(newOrFilter);
            } else {
                newOrFilters.push(storedOrFilter);
            }
        } else {
            newOrFilters.push(storedOrFilter);
        }
    });

    return { filters: newOrFilters };
}

interface TagCategoryExtractorsAndExpanders<T> {
    extractors: Array<DataExtractor<T, TagsData, IdAndName<string, string>>>;
    expanders: Array<ExtractorExpander<TagsData, IdAndName<string, string>>>;
}

function buildCategoryExtractorsAndExpandersFromTagsAndFilter<
    TAG extends AbstractTag,
    CAT extends AbstractTagCategory,
    ROW extends { tags: TagsData },
>(
    allTags: TagLookupData<TAG, CAT>,
    currentFilter: SimpleAndFilter | null
): TagCategoryExtractorsAndExpanders<ROW> {
    const tagNameLookup = createTagNameLookupFn(allTags);

    const extractors: Array<
        DataExtractor<ROW, TagsData, IdAndName<string, string>>
    > = [];
    const expanders: Array<
        ExtractorExpander<TagsData, IdAndName<string, string>>
    > = [];

    const allCategories: AbstractTagCategory[] = allTags.linkedResources.map(
        ({ id, name }) => ({ id, name })
    );
    // In case if one of the categories was removed, but it's still referred in the filter, we want to "resurrect" it by decoding
    // id and name from extractorId, so we can build an extractor and expander.
    if (currentFilter) {
        currentFilter.filters
            .map((f) => f.extractorId)
            .filter(DEFAULT_CATEGORY_METHODS.isCategoryExtractorId)
            .forEach((categoryExtractorId) => {
                const { id, name } =
                    DEFAULT_CATEGORY_METHODS.extractorIdToAbstractTagCategory(
                        categoryExtractorId
                    );
                const isFound = !!allCategories.find((c) => c.id === id);
                if (!isFound) {
                    allCategories.push({ id, name });
                }
            });
    }

    allCategories.forEach((category) => {
        const extractorId: string =
            DEFAULT_CATEGORY_METHODS.abstractTagCategoryToExtractorId({
                id: category.id,
                name: category.name,
            });

        const extractor: DataExtractor<
            ROW,
            TagsData,
            IdAndName<string, string>
        > = {
            id: extractorId,
            label: category.name,
            getValue: (row) => row.tags,
            // NOTE: comparison does not play any role, because category_ columns are not shown, but only used in search
            compare: (_a, _b) => 0,
            asString: (row) => {
                // asString is used in exporting tags to Excel:
                return row.tags.tags
                    .filter((tag) => tag.tagCategoryId === category.id)
                    .map((tag) => tag.name)
                    .join(", ");
            },
            customFilterTransformer: {
                getValue: (_row) => {
                    throw new Error(
                        `Unexpected call of customFilterTransformer.getValue() on ${extractorId} extractor.`
                    );
                },
                asString: tagNameLookup,
            },
        };

        const expander: ExtractorExpander<
            TagsData,
            IdAndName<string, string>
        > = {
            extractorId,
            expand: (tags: TagsData) => {
                const results: Array<PartialData<IdAndName<string, string>>> =
                    [];

                const categoryTags = tags.tags.filter(
                    (t) => t.tagCategoryId === category.id
                );
                categoryTags.forEach((tag) => {
                    const idAndName: IdAndName<string, string> = {
                        id: tag.id,
                        name: tag.name,
                    };
                    results.push({
                        partialId: `${category.name}-${tag.name}`,
                        expandedValue: idAndName,
                    });
                });
                return results;
            },
            // NOTE: comparison does not play any role, because category_ columns are not shown, but only used in search
            compare: (_a, _b) => 0,
            asString: (t: IdAndName<string, string>) => t.name,
            customFilterTransformer: {
                getValue: (idAndName) => idAndName,
                asString: tagNameLookup,
            },
        };

        extractors.push(extractor);
        expanders.push(expander);
    });

    return { extractors, expanders };
}

export {
    collectCategories,
    categoryExtractorId,
    isTagCategoriesExtractorId,
    buildCategoryExtractorsAndExpanders,
    buildTagCategoryExtractorExpanderForNonGroupedTagsWithIds,
    buildTagCategoryExtractorExpanderForNonGroupedTags,
    buildTagCategoryExtractorExpanderForGroupedTags,
    TAG_CATEGORIES_ID,
    CATEGORY_EXTRACTOR_ID_PREFIX,
    createCategoryNameLookupFn,
    buildCategoryExtractorsAndExpandersFromTagsAndFilter,
    createTagNameLookupFn,
    adjustFiltersWithCategoryExtractorId,
    encodeHex,
    decodeHex,
    getTagMethods,
    DEFAULT_CATEGORY_METHODS,
    type AbstractTag,
    type AbstractTagCategory,
    type TagCategoryExtractorsAndExpanders,
};
